import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, FileText } from "lucide-react";
import EnquiryDialog from "@/components/EnquiryDialog";

export default function ProductDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => { api.get(`/products/${id}`).then(r => setP(r.data)).catch(() => {}); }, [id]);
  if (!p) return <div className="py-20 text-center text-slate-500">Loading…</div>;

  return (
    <div className="mx-auto max-w-6xl px-6 py-12">
      <Link to="/products" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-[#0F2942]" data-testid="back-to-products">
        <ArrowLeft className="h-4 w-4" /> {t("product.back")}
      </Link>
      <div className="mt-6 grid gap-10 lg:grid-cols-2">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="aspect-[4/3] bg-slate-100">
            {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="h-full w-full object-cover" />}
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-700">{p.category}</div>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-[#0F2942] sm:text-4xl" style={{ fontFamily: "Outfit, sans-serif" }}>{p.name}</h1>
          <p className="mt-4 text-slate-600 leading-relaxed">{p.description}</p>
          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Price</div>
            <div className="mt-1 text-2xl font-bold text-[#0F2942]" style={{ fontFamily: "Outfit, sans-serif" }}>
              {p.price_on_request ? t("product.por") : `₹ ${p.price?.toLocaleString("en-IN")}`}
              {!p.price_on_request && <span className="ml-2 text-xs font-normal text-slate-500">+ GST {p.gst_rate}%</span>}
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button data-testid="product-enquire-btn" size="lg" onClick={() => setOpen(true)} className="bg-[#0F2942] text-white hover:bg-[#0A192F]">
              {t("product.enquire")}
            </Button>
            {p.brochure_url && (
              <a href={p.brochure_url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="lg" className="border-[#0F2942] text-[#0F2942] hover:bg-[#0F2942] hover:text-white">
                  <Download className="mr-2 h-4 w-4" /> {t("product.brochure")}
                </Button>
              </a>
            )}
          </div>
          {p.specs && Object.keys(p.specs).length > 0 && (
            <div className="mt-8">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#0F2942]">
                <FileText className="h-4 w-4" /> {t("product.specs")}
              </div>
              <div className="mt-3 divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
                {Object.entries(p.specs).map(([k, v]) => (
                  <div key={k} className="grid grid-cols-2 px-4 py-2.5 text-sm">
                    <span className="font-medium text-slate-500">{k}</span>
                    <span className="text-[#0F2942]">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <EnquiryDialog open={open} onOpenChange={setOpen} product={p} />
    </div>
  );
}
